(() => {
  'use strict';

  const P = window.Piggy || { routes: {}, strings: {}, bundle: { enabled: false, tiers: [] } };
  P.strings = P.strings || {};
  P.bundle = P.bundle || { enabled: false, tiers: [] };

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = () => Boolean(window.Shopify && window.Shopify.designMode);
  const onCartPage = () => Boolean($('[data-main-cart]'));
  const CHEVRON = '<svg class="icon select__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M6 9.5l6 6 6-6"/></svg>';

  const formatMoney = (cents) => {
    const value = Math.round(Number(cents) || 0);
    if (P.currency && P.shopCurrency && P.currency !== P.shopCurrency) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'sv', { style: 'currency', currency: P.currency }).format(value / 100);
      } catch (error) {
        /* fall through to the shop format */
      }
    }
    const format = (P.moneyFormat || '{{amount}} kr').replace(/<[^>]*>/g, '');
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const group = (decimals, thousands, decimal) => {
      const [whole, fraction] = (value / 100).toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? grouped + decimal + fraction : grouped;
    };
    const formats = {
      amount: () => group(2, ',', '.'),
      amount_no_decimals: () => group(0, ',', '.'),
      amount_with_comma_separator: () => group(2, '.', ','),
      amount_no_decimals_with_comma_separator: () => group(0, '.', ','),
      amount_with_apostrophe_separator: () => group(2, "'", '.'),
      amount_no_decimals_with_space_separator: () => group(0, ' ', ','),
      amount_with_space_separator: () => group(2, ' ', ','),
      amount_with_period_and_space_separator: () => group(2, ' ', '.')
    };
    const kind = match && formats[match[1]] ? match[1] : 'amount';
    const formatted = formats[kind]();
    return match ? format.replace(match[0], formatted) : formatted;
  };

  const discountOf = (amount, pct) => Math.round((amount * (Number(pct) || 0)) / 100);

  const tierFor = (count) => {
    let best = { qty: 0, pct: 0 };
    (P.bundle.tiers || []).forEach((tier) => {
      if (count >= tier.qty && tier.pct >= best.pct) best = tier;
    });
    return best;
  };

  const postJSON = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    });
    let data = {};
    try {
      data = await response.json();
    } catch (error) {
      data = {};
    }
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || P.strings.error);
    }
    return data;
  };

  const Toast = {
    timer: null,
    show(message) {
      if (!message) return;
      const host = $('dialog[open]') || document.body;
      let toast = document.getElementById('PiggyToast');
      if (!toast) {
        toast = document.createElement('div');
        toast.id = 'PiggyToast';
        toast.className = 'toast';
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');
      }
      if (toast.parentElement !== host) host.append(toast);
      toast.textContent = message;
      requestAnimationFrame(() => toast.classList.add('is-visible'));
      clearTimeout(this.timer);
      this.timer = setTimeout(() => toast.classList.remove('is-visible'), 4500);
    }
  };

  const Drawer = {
    open(dialog, opener) {
      if (!dialog) return;
      if (dialog.open) {
        dialog.classList.remove('is-closing');
        return;
      }
      dialog._opener = opener || document.activeElement;
      dialog.classList.remove('is-closing');
      dialog.showModal();
      document.documentElement.classList.add('has-drawer');
      dialog.scrollTop = 0;
      dialog.scrollLeft = 0;
      const focusTarget = $('[data-predictive-input]', dialog);
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    },
    close(dialog) {
      if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        dialog.classList.remove('is-closing');
        dialog.close();
      };
      if (reducedMotion()) {
        finish();
        return;
      }
      dialog.classList.add('is-closing');
      const panel = $('.drawer__panel, .search-modal__panel', dialog);
      if (panel) panel.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 420);
    },
    bind(dialog) {
      if (dialog._piggyBound) return;
      dialog._piggyBound = true;
      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        Drawer.close(dialog);
      });
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) Drawer.close(dialog);
      });
      dialog.addEventListener('close', () => {
        if (!$('dialog[open]')) document.documentElement.classList.remove('has-drawer');
        const opener = dialog._opener;
        dialog._opener = null;
        if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
      });
    }
  };

  const Cart = {
    drawer() {
      return document.getElementById('CartDrawer');
    },
    sectionIds() {
      const ids = ['cart-icon-bubble'];
      if (this.drawer()) ids.push('cart-drawer');
      $$('[data-main-cart]').forEach((element) => ids.push(element.dataset.sectionId));
      return ids;
    },
    payload(extra) {
      return Object.assign({ sections: this.sectionIds().join(','), sections_url: window.location.pathname }, extra);
    },
    setLoading(state) {
      $$('[data-cart-region]').forEach((element) => element.classList.toggle('is-loading', state));
    },
    render(sections) {
      if (!sections) return;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        if (id === 'cart-icon-bubble') {
          const fresh = $('[data-cart-count]', doc);
          if (!fresh) return;
          $$('[data-cart-count]').forEach((element) => {
            const changed = element.textContent.trim() !== fresh.textContent.trim();
            element.textContent = fresh.textContent.trim();
            element.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
            if (changed) {
              element.classList.remove('is-bumped');
              void element.offsetWidth;
              element.classList.add('is-bumped');
            }
          });
          return;
        }
        const target = $(`[data-cart-region="${id}"]`);
        const source = $(`[data-cart-region="${id}"]`, doc);
        if (!target || !source) return;
        const scroller = $('[data-drawer-scroll]', target);
        const scrollTop = scroller ? scroller.scrollTop : 0;
        target.innerHTML = source.innerHTML;
        const newScroller = $('[data-drawer-scroll]', target);
        if (newScroller) newScroller.scrollTop = scrollTop;
      });
      document.dispatchEvent(new CustomEvent('piggy:cart-updated'));
    },
    async add(items, options = {}) {
      const open = options.open !== false && !onCartPage();
      const data = await postJSON(`${P.routes.cartAdd}.js`, this.payload({ items }));
      this.render(data.sections);
      if (open) this.open();
      return data;
    },
    async change(line, quantity) {
      this.setLoading(true);
      try {
        const data = await postJSON(`${P.routes.cartChange}.js`, this.payload({ line, quantity: Math.max(0, quantity) }));
        this.render(data.sections);
        return data;
      } catch (error) {
        Toast.show(error.message);
        await this.refresh();
        return null;
      } finally {
        this.setLoading(false);
      }
    },
    async update(body) {
      const data = await postJSON(`${P.routes.cartUpdate}.js`, this.payload(body));
      this.render(data.sections);
      return data;
    },
    async refresh() {
      try {
        const response = await fetch(`${window.location.pathname}?sections=${this.sectionIds().join(',')}`);
        this.render(await response.json());
      } catch (error) {
        /* keep the current markup */
      }
    },
    async get() {
      const response = await fetch(`${P.routes.cart}.js`, { headers: { Accept: 'application/json' } });
      return response.json();
    },
    open() {
      const drawer = this.drawer();
      if (P.cartType !== 'drawer' || !drawer) {
        window.location.href = P.routes.cart;
        return;
      }
      Drawer.open(drawer);
    }
  };

  const copyText = async (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (error) {
        /* in-app browsers can refuse this, so try the older way */
      }
    }
    const field = document.createElement('textarea');
    field.value = text;
    field.readOnly = true;
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.append(field);
    field.select();
    field.setSelectionRange(0, text.length);
    let copied = false;
    try {
      copied = document.execCommand('copy');
    } catch (error) {
      copied = false;
    }
    field.remove();
    return copied;
  };

  const copyCode = async (button) => {
    const label = button.dataset.label || button.textContent;
    button.dataset.label = label;
    if (!(await copyText(button.dataset.copyCode))) return;
    button.textContent = button.dataset.copiedLabel;
    button.classList.add('is-copied');
    window.clearTimeout(button.copyTimer);
    button.copyTimer = window.setTimeout(() => {
      button.textContent = label;
      button.classList.remove('is-copied');
    }, 2500);
  };

  const setDiscountError = (message) => {
    $$('[data-discount-error]').forEach((element) => {
      element.textContent = message || '';
      element.hidden = !message;
    });
  };

  const applyDiscount = async (code) => {
    Cart.setLoading(true);
    setDiscountError('');
    try {
      const current = await Cart.get();
      const existing = (current.discount_codes || []).filter((entry) => entry.applicable !== false).map((entry) => entry.code);
      if (existing.some((entry) => entry.toLowerCase() === code.toLowerCase())) {
        await Cart.refresh();
        return;
      }
      const data = await Cart.update({ discount: existing.concat(code).join(',') });
      if (Array.isArray(data.discount_codes)) {
        const applied = data.discount_codes.find((entry) => entry.code.toLowerCase() === code.toLowerCase());
        if (!applied || applied.applicable === false) {
          await Cart.update({ discount: existing.join(',') });
          setDiscountError(P.strings.discountInvalid);
        }
      }
    } catch (error) {
      setDiscountError(error.message || P.strings.discountInvalid);
    } finally {
      Cart.setLoading(false);
    }
  };

  const removeDiscount = async (code) => {
    Cart.setLoading(true);
    try {
      const current = await Cart.get();
      const remaining = (current.discount_codes || [])
        .map((entry) => entry.code)
        .filter((entry) => entry.toLowerCase() !== code.toLowerCase());
      await Cart.update({ discount: remaining.join(',') });
    } catch (error) {
      Toast.show(error.message);
    } finally {
      Cart.setLoading(false);
    }
  };

  const addSingle = async (button, variantId) => {
    if (!variantId || button.classList.contains('is-loading')) return;
    if (!$('.spinner', button)) button.insertAdjacentHTML('beforeend', '<span class="spinner" aria-hidden="true"></span>');
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    try {
      await Cart.add([{ id: Number(variantId), quantity: 1 }]);
      if (document.contains(button)) {
        button.classList.add('is-added');
        setTimeout(() => button.classList.remove('is-added'), 1400);
      }
    } catch (error) {
      Toast.show(error.message);
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  };

  class PiggyProduct extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      const json = $('[data-product-json]', this);
      this.product = json ? JSON.parse(json.textContent) : { variants: [] };
      this.form = $('[data-product-form]', this);
      this.variantInput = $('[data-variant-id-input]', this);
      this.quantityInput = $('[data-quantity-input]', this);
      this.button = $('[data-add-to-cart]', this);
      this.buttonLabel = $('[data-atc-label]', this);
      this.errorBox = $('[data-atc-error]', this);
      this.perItem = this.dataset.variantPerItem === 'true';
      this.lowStock = Number(this.dataset.lowStock || 0);
      const section = this.closest('.product-section');
      this.sticky = section ? $('[data-sticky-atc]', section) : null;
      this.variant = this.findVariant(this.selectedOptions()) || this.variantFromInput();

      this.addEventListener('change', (event) => this.onChange(event));
      if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.bindSticky();
      this.updateAvailability();
      this.updateStock();
      this.updateBundle(false);
      this.updateTotals();
    }

    selectedOptions() {
      const values = [];
      $$('[data-option-index]', this).forEach((input) => {
        const index = Number(input.dataset.optionIndex);
        if (input.tagName === 'SELECT') values[index] = input.value;
        else if (input.checked) values[index] = input.value;
      });
      return values;
    }

    findVariant(options) {
      if (!options.length) return null;
      for (let index = 0; index < options.length; index += 1) {
        if (options[index] === undefined) return null;
      }
      return this.product.variants.find((variant) => variant.options.every((value, index) => value === options[index])) || null;
    }

    variantFromInput() {
      const id = this.variantInput ? Number(this.variantInput.value) : 0;
      return this.product.variants.find((variant) => variant.id === id) || this.product.variants[0] || null;
    }

    onChange(event) {
      const target = event.target;
      if (target.matches('[data-option-index]')) {
        this.onOptionChange();
      } else if (target.matches('[data-tier-input]')) {
        this.updateBundle(true);
        this.updateTotals();
      } else if (target.matches('[data-addon], [data-tier-variant]')) {
        this.updateTotals();
      }
    }

    onOptionChange() {
      const options = this.selectedOptions();
      $$('[data-option-label]', this).forEach((label) => {
        label.textContent = options[Number(label.dataset.optionLabel)] || '';
      });
      this.variant = this.findVariant(options);
      this.updateAvailability();
      if (!this.variant) {
        this.setButton(false, P.strings.unavailable);
        return;
      }
      if (this.variantInput) this.variantInput.value = this.variant.id;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', this.variant.id);
      window.history.replaceState({}, '', url.toString());
      this.updatePrice();
      this.updateStock();
      this.updateBundle(true);
      this.updateTotals();
      if (this.variant.media_id) {
        this.dispatchEvent(new CustomEvent('piggy:media', { bubbles: true, detail: { mediaId: this.variant.media_id } }));
      }
      document.dispatchEvent(new CustomEvent('piggy:variant-change', { detail: { variant: this.variant, product: this } }));
    }

    updateAvailability() {
      const selected = this.selectedOptions();
      $$('.variants__radio[data-option-index]', this).forEach((input) => {
        const candidate = selected.slice();
        candidate[Number(input.dataset.optionIndex)] = input.value;
        const match = this.product.variants.find((variant) => variant.options.every((value, index) => value === candidate[index]));
        const label = input.nextElementSibling;
        if (label) label.classList.toggle('is-unavailable', !match || !match.available);
      });
    }

    updatePrice() {
      const variant = this.variant;
      const onSale = variant.compare_at_price > variant.price;
      const wrap = $('[data-price-wrap]', this);
      if (wrap) wrap.classList.toggle('price--sale', onSale);
      const price = $('[data-price]', this);
      if (price) price.textContent = formatMoney(variant.price);
      const compare = $('[data-compare]', this);
      if (compare) {
        compare.hidden = !onSale;
        compare.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
      }
      const badge = $('[data-sale-badge]', this);
      if (badge) {
        badge.hidden = !onSale;
        if (onSale) badge.textContent = (P.strings.saveAmount || '[amount]').replace('[amount]', formatMoney(variant.compare_at_price - variant.price));
      }
      const unit = $('[data-unit-price]', this);
      if (unit) {
        unit.hidden = !variant.unit_price;
        unit.textContent = variant.unit_price || '';
      }
      const sku = $('[data-sku]', this);
      if (sku) {
        sku.hidden = !variant.sku;
        const skuValue = $('[data-sku-value]', sku);
        if (skuValue) skuValue.textContent = variant.sku || '';
      }
    }

    updateStock() {
      const stock = $('[data-stock]', this);
      const variant = this.variant;
      if (!stock || !variant) return;
      const tracked = variant.inventory_management === 'shopify';
      let state = 'in';
      let message = P.strings.inStock;
      if (!variant.available) {
        state = 'out';
        message = P.strings.outOfStock;
      } else if (tracked && variant.inventory_quantity <= 0) {
        state = 'low';
        message = P.strings.backorder;
      } else if (tracked && this.lowStock > 0 && variant.inventory_quantity <= this.lowStock) {
        state = 'low';
        message = (P.strings.lowStock || '').replace('[quantity]', variant.inventory_quantity);
      }
      stock.dataset.state = state;
      const text = $('[data-stock-text]', stock);
      if (text && message) text.textContent = message;
    }

    tiers() {
      return $$('[data-tier]', this);
    }

    selectedTier() {
      const input = $('[data-tier-input]:checked', this);
      return input ? input.closest('[data-tier]') : null;
    }

    updateBundle(rebuild) {
      const tiers = this.tiers();
      if (!tiers.length) return;
      const selected = this.selectedTier();
      tiers.forEach((tier) => {
        const active = tier === selected;
        const qty = Number(tier.dataset.qty);
        const pct = Number(tier.dataset.pct);
        tier.classList.toggle('is-selected', active);
        if (this.variant) {
          const base = this.variant.price * qty;
          const total = base - discountOf(base, pct);
          const price = $('[data-tier-price]', tier);
          if (price) price.textContent = formatMoney(total);
          const compare = $('[data-tier-compare]', tier);
          if (compare) compare.textContent = formatMoney(base);
          const sub = $('[data-tier-sub]', tier);
          if (sub && pct > 0) {
            sub.textContent = (P.strings.saveEach || '')
              .replace('[pct]', pct)
              .replace('[each]', formatMoney(Math.round(total / qty)));
          }
        }
        const holder = $('[data-tier-variants]', tier);
        if (!holder) return;
        const needsPicker = active && this.perItem && qty > 1 && this.product.variants.length > 1;
        if (needsPicker) {
          if (rebuild || !holder.children.length) this.buildTierVariants(holder, qty);
          holder.hidden = false;
        } else {
          holder.hidden = true;
          holder.innerHTML = '';
        }
      });
      if (this.quantityInput && selected) this.quantityInput.value = selected.dataset.qty;
    }

    buildTierVariants(holder, qty) {
      holder.innerHTML = '';
      const available = this.product.variants.filter((variant) => variant.available);
      for (let index = 1; index <= qty; index += 1) {
        const row = document.createElement('label');
        row.className = 'bundle__variant-row';
        const caption = document.createElement('span');
        caption.textContent = (P.strings.itemN || '[n]').replace('[n]', index);
        const wrap = document.createElement('div');
        wrap.className = 'select';
        const select = document.createElement('select');
        select.className = 'select__input';
        select.dataset.tierVariant = '';
        available.forEach((variant) => {
          const option = new Option(variant.title, variant.id);
          if (this.variant && variant.id === this.variant.id) option.selected = true;
          select.add(option);
        });
        wrap.append(select);
        wrap.insertAdjacentHTML('beforeend', CHEVRON);
        row.append(caption, wrap);
        holder.append(row);
      }
    }

    lineItems() {
      if (!this.variant) return [];
      const tier = this.selectedTier();
      const qty = tier ? Number(tier.dataset.qty) : 1;
      const holder = tier ? $('[data-tier-variants]', tier) : null;
      const selects = holder && !holder.hidden ? $$('[data-tier-variant]', holder) : [];
      if (selects.length) {
        return selects
          .map((select) => this.product.variants.find((variant) => String(variant.id) === select.value))
          .filter(Boolean)
          .map((variant) => ({ id: variant.id, quantity: 1, price: variant.price, available: variant.available }));
      }
      return [{ id: this.variant.id, quantity: qty, price: this.variant.price, available: this.variant.available }];
    }

    addonItems() {
      return $$('[data-addon]:checked', this).map((input) => ({
        id: Number(input.dataset.variantId),
        quantity: 1,
        price: Number(input.dataset.price),
        available: true
      }));
    }

    updateTotals() {
      const items = this.lineItems();
      const tier = this.selectedTier();
      const pct = tier ? Number(tier.dataset.pct) : 0;
      const base = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const mainTotal = base - discountOf(base, pct);
      if (tier && items.length) {
        const price = $('[data-tier-price]', tier);
        if (price) price.textContent = formatMoney(mainTotal);
        const compare = $('[data-tier-compare]', tier);
        if (compare) compare.textContent = formatMoney(base);
      }
      const total = mainTotal + this.addonItems().reduce((sum, item) => sum + item.price, 0);
      const available = items.length > 0 && items.every((item) => item.available);
      if (!this.variant) {
        this.setButton(false, P.strings.unavailable);
      } else if (!available) {
        this.setButton(false, P.strings.soldOut);
      } else {
        this.setButton(true, `${P.strings.addToCart} — ${formatMoney(total)}`);
      }
      if (this.sticky) {
        const stickyPrice = $('[data-sticky-price]', this.sticky);
        if (stickyPrice) stickyPrice.textContent = formatMoney(total);
      }
    }

    setButton(enabled, label) {
      if (!this.button) return;
      this.button.disabled = !enabled;
      if (this.buttonLabel && label) this.buttonLabel.textContent = label;
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!this.button || this.button.disabled || this.button.classList.contains('is-loading')) return;
      const merged = [];
      this.lineItems()
        .concat(this.addonItems())
        .forEach((item) => {
          const existing = merged.find((entry) => entry.id === item.id);
          if (existing) existing.quantity += item.quantity;
          else merged.push({ id: item.id, quantity: item.quantity });
        });
      if (!merged.length) return;
      if (this.errorBox) this.errorBox.hidden = true;
      this.button.classList.add('is-loading');
      this.button.setAttribute('aria-busy', 'true');
      try {
        await Cart.add(merged);
      } catch (error) {
        if (this.errorBox) {
          this.errorBox.textContent = error.message;
          this.errorBox.hidden = false;
        } else {
          Toast.show(error.message);
        }
      } finally {
        this.button.classList.remove('is-loading');
        this.button.removeAttribute('aria-busy');
      }
    }

    bindSticky() {
      if (!this.sticky || !this.button || !('IntersectionObserver' in window)) return;
      const stickyButton = $('[data-sticky-atc-button]', this.sticky);
      if (stickyButton) {
        stickyButton.addEventListener('click', () => {
          if (this.button.disabled) {
            this.button.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
          if (this.form && typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
          else this.button.click();
        });
      }
      const observer = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.sticky.classList.toggle('is-visible', show);
        this.sticky.setAttribute('aria-hidden', String(!show));
        if (stickyButton) stickyButton.tabIndex = show ? 0 : -1;
      });
      observer.observe(this.button);
    }
  }

  class PiggyGallery extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.viewport = $('[data-gallery-viewport]', this);
      this.slides = $$('.gallery__slide', this);
      if (!this.viewport || this.slides.length < 2) return;
      $$('[data-thumb]', this).forEach((thumb) => {
        thumb.addEventListener('click', () => this.go(thumb.dataset.thumb));
      });
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.setActive(entry.target.dataset.mediaId);
            });
          },
          { root: this.viewport, threshold: 0.6 }
        );
        this.slides.forEach((slide) => observer.observe(slide));
      }
      const product = this.closest('piggy-product');
      if (product) product.addEventListener('piggy:media', (event) => this.go(String(event.detail.mediaId)));
      const active = $('.gallery__slide.is-active', this);
      if (active && active !== this.slides[0]) this.go(active.dataset.mediaId, false);
    }

    go(id, smooth = true) {
      const slide = this.slides.find((item) => item.dataset.mediaId === String(id));
      if (!slide) return;
      this.viewport.scrollTo({ left: slide.offsetLeft, behavior: smooth && !reducedMotion() ? 'smooth' : 'auto' });
      this.setActive(id);
    }

    setActive(id) {
      const key = String(id);
      this.slides.forEach((slide) => slide.classList.toggle('is-active', slide.dataset.mediaId === key));
      $$('[data-thumb]', this).forEach((thumb) => thumb.classList.toggle('is-active', thumb.dataset.thumb === key));
      $$('[data-dot]', this).forEach((dot) => dot.classList.toggle('is-active', dot.dataset.dot === key));
    }
  }

  if (!customElements.get('piggy-product')) customElements.define('piggy-product', PiggyProduct);
  if (!customElements.get('piggy-gallery')) customElements.define('piggy-gallery', PiggyGallery);

  const initFbt = (root) => {
    if (root._ready) return;
    root._ready = true;
    const button = $('[data-fbt-add]', root);
    if (!button) return;
    if ($$('[data-fbt-item]', root).length < 2) {
      root.hidden = true;
      return;
    }

    const label = $('[data-fbt-label]', root);
    const totalEl = $('[data-fbt-total]', root);
    const compareEl = $('[data-fbt-compare]', root);
    const saveEl = $('[data-fbt-save]', root);

    const chosen = () =>
      $$('[data-fbt-item]', root).filter((item) => {
        if (item.hasAttribute('data-fbt-main')) return true;
        const check = $('[data-fbt-check]', item);
        return check && check.checked;
      });

    const update = () => {
      const items = chosen();
      const eligible = items.filter((item) => item.dataset.eligible === 'true').length;
      const tier = P.bundle.enabled ? tierFor(eligible) : { pct: 0 };
      let base = 0;
      let discount = 0;
      items.forEach((item) => {
        const price = Number(item.dataset.price);
        base += price;
        if (item.dataset.eligible === 'true') discount += discountOf(price, tier.pct);
      });
      if (label) label.textContent = items.length > 1 ? `${P.strings.addAll} ${items.length}` : P.strings.addToCart;
      if (totalEl) totalEl.textContent = `— ${formatMoney(base - discount)}`;
      if (compareEl) {
        compareEl.hidden = discount <= 0;
        compareEl.textContent = discount > 0 ? formatMoney(base) : '';
      }
      if (saveEl) {
        saveEl.hidden = discount <= 0;
        saveEl.textContent = discount > 0 ? (P.strings.savePct || '').replace('[pct]', tier.pct) : '';
      }
    };

    root.addEventListener('change', update);
    document.addEventListener('piggy:variant-change', (event) => {
      const main = $('[data-fbt-main]', root);
      const variant = event.detail && event.detail.variant;
      if (!main || !variant || !root.closest('piggy-product') || event.detail.product !== root.closest('piggy-product')) return;
      main.dataset.variantId = variant.id;
      main.dataset.price = variant.price;
      const price = $('[data-fbt-main-price]', main);
      if (price) price.textContent = formatMoney(variant.price);
      update();
    });

    button.addEventListener('click', async () => {
      if (button.classList.contains('is-loading')) return;
      const items = chosen().map((item) => ({ id: Number(item.dataset.variantId), quantity: 1 }));
      button.classList.add('is-loading');
      try {
        await Cart.add(items);
      } catch (error) {
        Toast.show(error.message);
      } finally {
        button.classList.remove('is-loading');
      }
    });

    update();
  };

  const initDelivery = (root) => {
    const estimate = $('[data-delivery-estimate]', root);
    if (!estimate) return;
    const min = Math.max(0, Number(root.dataset.min) || 0);
    const max = Math.max(min, Number(root.dataset.max) || min);
    const cutoff = Number(root.dataset.cutoff) || 14;
    const now = new Date();
    const isWeekend = now.getDay() === 0 || now.getDay() === 6;
    const afterCutoff = now.getHours() >= cutoff;
    const addBusinessDays = (date, days) => {
      const result = new Date(date);
      let added = 0;
      while (added < days) {
        result.setDate(result.getDate() + 1);
        if (result.getDay() !== 0 && result.getDay() !== 6) added += 1;
      }
      return result;
    };
    const start = isWeekend || afterCutoff ? addBusinessDays(now, 1) : now;
    const from = addBusinessDays(start, min);
    const to = addBusinessDays(start, max);
    let formatter;
    try {
      formatter = new Intl.DateTimeFormat(P.locale || 'sv', { weekday: 'short', day: 'numeric', month: 'short' });
    } catch (error) {
      formatter = new Intl.DateTimeFormat('sv', { weekday: 'short', day: 'numeric', month: 'short' });
    }
    const range = from.toDateString() === to.toDateString() ? formatter.format(from) : `${formatter.format(from)} – ${formatter.format(to)}`;
    estimate.innerHTML = (P.strings.deliveryEstimate || '[range]').replace('[range]', range);

    const cutoffEl = $('[data-delivery-cutoff]', root);
    if (cutoffEl && root.dataset.showCutoff === 'true' && !isWeekend && !afterCutoff) {
      const end = new Date(now);
      end.setHours(cutoff, 0, 0, 0);
      const diff = end - now;
      const hours = Math.floor(diff / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const time = hours > 0 ? `${hours} ${P.strings.hoursShort} ${minutes} ${P.strings.minutesShort}` : `${minutes} ${P.strings.minutesShort}`;
      cutoffEl.innerHTML = (P.strings.deliveryCutoff || '[time]').replace('[time]', time);
      cutoffEl.hidden = false;
    }
  };

  const initCountdown = (root) => {
    if (root._ready) return;
    root._ready = true;
    const doors = $('[data-countdown-doors]', root);
    const merry = $('[data-countdown-merry]', root);
    const units = {
      days: $('[data-unit="days"]', root),
      hours: $('[data-unit="hours"]', root),
      minutes: $('[data-unit="minutes"]', root),
      seconds: $('[data-unit="seconds"]', root)
    };
    const pad = (value) => String(value).padStart(2, '0');
    const tick = () => {
      const now = new Date();
      const year = now.getFullYear();
      const eve = new Date(year, 11, 24, 0, 0, 0);
      const afterChristmas = new Date(year, 11, 27, 0, 0, 0);
      if (now >= eve && now < afterChristmas) {
        if (doors) doors.hidden = true;
        if (merry) merry.hidden = false;
        return;
      }
      const target = now < eve ? eve : new Date(year + 1, 11, 24, 0, 0, 0);
      const diff = Math.max(0, target - now);
      if (units.days) units.days.textContent = Math.floor(diff / 86400000);
      if (units.hours) units.hours.textContent = pad(Math.floor((diff % 86400000) / 3600000));
      if (units.minutes) units.minutes.textContent = pad(Math.floor((diff % 3600000) / 60000));
      if (units.seconds) units.seconds.textContent = pad(Math.floor((diff % 60000) / 1000));
    };
    tick();
    setInterval(tick, 1000);
  };

  const initAnnouncement = (root) => {
    if (root._ready) return;
    root._ready = true;
    const items = $$('.announcement__item', root);
    if (items.length < 2) return;
    let index = 0;
    const interval = Number(root.dataset.interval) || 4000;
    const next = () => {
      items[index].classList.remove('is-active');
      index = (index + 1) % items.length;
      items[index].classList.add('is-active');
    };
    let timer = setInterval(next, interval);
    root.addEventListener('mouseenter', () => clearInterval(timer));
    root.addEventListener('mouseleave', () => {
      clearInterval(timer);
      timer = setInterval(next, interval);
    });
  };

  const initHeader = () => {
    const header = $('[data-header]');
    if (!header) return;
    const setHeight = () => document.documentElement.style.setProperty('--header-h', `${header.offsetHeight}px`);
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    setHeight();
    onScroll();
    window.addEventListener('resize', setHeight);
    window.addEventListener('scroll', onScroll, { passive: true });
  };

  const initSearch = () => {
    const input = $('[data-predictive-input]');
    const results = $('[data-predictive-results]');
    if (!input || !results || !P.routes.predictiveSearch) return;
    let timer;
    let controller;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const query = input.value.trim();
      if (query.length < 2) {
        results.innerHTML = '';
        return;
      }
      timer = setTimeout(async () => {
        if (controller) controller.abort();
        controller = new AbortController();
        const params = new URLSearchParams({
          q: query,
          section_id: 'predictive-search',
          'resources[type]': 'product,collection,page,query',
          'resources[limit]': '6',
          'resources[options][unavailable_products]': 'last'
        });
        try {
          const response = await fetch(`${P.routes.predictiveSearch}?${params.toString()}`, { signal: controller.signal });
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const content = $('[data-predictive-content]', doc);
          results.innerHTML = content ? content.outerHTML : '';
        } catch (error) {
          if (error.name !== 'AbortError') results.innerHTML = '';
        }
      }, 220);
    });
  };

  const initReveal = (root = document) => {
    const targets = $$('[data-reveal]:not(.is-visible)', root);
    if (!targets.length) return;
    const animate = document.body.classList.contains('has-animations') && 'IntersectionObserver' in window && !reducedMotion() && !designMode();
    if (!animate) {
      targets.forEach((target) => target.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.05 }
    );
    targets.forEach((target, index) => {
      target.style.transitionDelay = `${(index % 4) * 70}ms`;
      observer.observe(target);
    });
  };

  const closeFilters = () => {
    $$('[data-filters-panel].is-open').forEach((panel) => panel.classList.remove('is-open'));
    if (!$('dialog[open]')) document.documentElement.classList.remove('has-drawer');
  };

  const initAll = (root = document) => {
    $$('[data-announcement]', root).forEach(initAnnouncement);
    $$('[data-delivery]', root).forEach(initDelivery);
    $$('[data-countdown]', root).forEach(initCountdown);
    $$('[data-fbt]', root).forEach(initFbt);
    $$('dialog[data-drawer]', root).forEach((dialog) => Drawer.bind(dialog));
    initReveal(root);
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.drawerOpen);
      if (dialog && !(opener.dataset.drawerOpen === 'CartDrawer' && onCartPage())) {
        event.preventDefault();
        Drawer.bind(dialog);
        Drawer.open(dialog, opener);
      }
      return;
    }

    const closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      Drawer.close(closer.closest('dialog'));
      return;
    }

    const cartChange = event.target.closest('[data-cart-change]');
    if (cartChange) {
      event.preventDefault();
      Cart.change(Number(cartChange.dataset.line), Number(cartChange.dataset.quantity));
      return;
    }

    const upsell = event.target.closest('[data-upsell-add]');
    if (upsell) {
      event.preventDefault();
      const item = upsell.closest('.upsell__item');
      const select = item ? $('[data-upsell-variant]', item) : null;
      addSingle(upsell, select ? select.value : upsell.dataset.variantId);
      return;
    }

    const quick = event.target.closest('[data-quick-add]');
    if (quick) {
      event.preventDefault();
      addSingle(quick, quick.dataset.variantId);
      return;
    }

    const removeCode = event.target.closest('[data-discount-remove]');
    if (removeCode) {
      event.preventDefault();
      removeDiscount(removeCode.dataset.discountRemove);
      return;
    }

    const copyButton = event.target.closest('[data-copy-code]');
    if (copyButton) {
      event.preventDefault();
      copyCode(copyButton);
      return;
    }

    const mapButton = event.target.closest('[data-map-load]');
    if (mapButton) {
      const map = mapButton.closest('[data-map]');
      if (!map) return;
      const iframe = document.createElement('iframe');
      iframe.src = map.dataset.src;
      iframe.title = mapButton.textContent.trim();
      iframe.loading = 'lazy';
      iframe.referrerPolicy = 'no-referrer-when-downgrade';
      iframe.allowFullscreen = true;
      map.innerHTML = '';
      map.append(iframe);
      return;
    }

    const filtersOpen = event.target.closest('[data-filters-open]');
    if (filtersOpen) {
      const panel = document.getElementById(filtersOpen.getAttribute('aria-controls'));
      if (panel) {
        panel.classList.add('is-open');
        document.documentElement.classList.add('has-drawer');
      }
      return;
    }

    if (event.target.closest('[data-filters-close]')) {
      closeFilters();
      return;
    }

    const recover = event.target.closest('[data-recover-open]');
    if (recover) {
      const details = document.getElementById('recover');
      if (details) {
        event.preventDefault();
        details.open = true;
        details.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  });

  document.addEventListener('change', (event) => {
    const quantity = event.target.closest('[data-cart-quantity]');
    if (quantity) {
      Cart.change(Number(quantity.dataset.line), parseInt(quantity.value, 10) || 0);
      return;
    }

    const note = event.target.closest('[data-cart-note]');
    if (note) {
      postJSON(`${P.routes.cartUpdate}.js`, { note: note.value }).catch(() => {});
      return;
    }

    const auto = event.target.closest('[data-auto-submit]');
    if (auto && auto.form) {
      const isSort = auto.name === 'sort_by';
      if (isSort || window.matchMedia('(min-width: 990px)').matches) {
        if (typeof auto.form.requestSubmit === 'function') auto.form.requestSubmit();
        else auto.form.submit();
      }
    }
  });

  document.addEventListener('submit', (event) => {
    const discountForm = event.target.closest('[data-discount-form]');
    if (discountForm) {
      event.preventDefault();
      const input = $('[data-discount-input]', discountForm);
      const code = input ? input.value.trim() : '';
      if (code) applyDiscount(code);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && $('[data-filters-panel].is-open')) closeFilters();
  });

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) Cart.refresh();
  });

  window.PiggyCart = Cart;

  initHeader();
  initSearch();
  initAll(document);
})();
